import React, { useState } from "react";
import { Tool, CallToolResult } from "@modelcontextprotocol/sdk/types.js";

import { useMCP } from "mcp/hooks/useMCP";

export const MCPToolsPanel: React.FC = () => {
    const { isCindraConnected, tools, callTool } = useMCP();
    const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
    const [toolArgs, setToolArgs] = useState<string>("{}");
    const [toolResult, setToolResult] = useState<CallToolResult | null>(null);
    const [isExecuting, setIsExecuting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleToolSelect = (tool: Tool) => {
        setSelectedTool(tool);
        setToolArgs("{}");
        setToolResult(null);
        setError(null);
    };

    const handleExecuteTool = async () => {
        if (!selectedTool) return;

        setIsExecuting(true);
        setError(null);
        setToolResult(null);

        try {
            const args = toolArgs.trim() ? JSON.parse(toolArgs) : {};
            const result = await callTool(selectedTool.name, args);
            setToolResult(result);
        }
        catch (err) {
            const errorMessage = err instanceof Error ? err.message : "Failed to execute tool";
            setError(errorMessage);
        }
        finally {
            setIsExecuting(false);
        }
    };

    const formatToolArguments = (inputSchema: any) => {
        if (!inputSchema?.properties) {
            return <p className="text-sm text-gray-500 italic">No arguments required</p>;
        }

        const properties = inputSchema.properties;
        const required = inputSchema.required || [];

        return (
            <div className="space-y-3">
                <h5 className="text-sm font-semibold text-gray-800">Arguments:</h5>
                {
                    Object.entries(properties).map(([argName, argSchema]: [string, any]) => (
                        <div key={argName} className="border-l-2 border-gray-200 pl-3">
                            <div className="flex items-center gap-2 flex-wrap">
                                <code className="text-sm font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                                    {argName}
                                </code>
                                <span className="text-xs text-gray-500">
                                    {argSchema.type || "any"}
                                </span>
                                {
                                    argSchema.default !== undefined && (
                                        <span className="text-xs text-gray-600">
                                            default: <code className="bg-gray-100 px-1 py-0.5 rounded">
                                                {JSON.stringify(argSchema.default)}
                                            </code>
                                        </span>
                                    )
                                }
                                {
                                    required.includes(argName) && (
                                        <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                                            required
                                        </span>
                                    )
                                }
                            </div>
                            {
                                argSchema.description && (
                                    <p className="text-sm text-gray-600 mt-1">{argSchema.description}</p>
                                )
                            }
                            {
                                argSchema.enum && (
                                    <div className="mt-1">
                                        <span className="text-xs text-gray-500">Values: </span>
                                        <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">
                                            {argSchema.enum.join(" | ")}
                                        </code>
                                    </div>
                                )
                            }
                        </div>
                    ))
                }
            </div>
        );
    };

    if (!isCindraConnected) {
        return (
            <div className="bg-white border border-gray-300 rounded-lg shadow p-6">
                <p className="text-gray-500">Connect to an MCP server to see available tools.</p>
            </div>
        );
    }

    return (
        <div className="bg-white border border-gray-300 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                MCP Tools ({tools.length})
            </h3>
            {
                tools.length === 0
                    ? (
                        <p className="text-gray-500">No tools available from the connected server.</p>
                    )
                    : (
                        <div className="space-y-4">
                            <div>
                                <label htmlFor="toolSelect" className="block text-sm font-medium text-gray-700 mb-1">
                                    Select Tool
                                </label>
                                <select
                                    id="toolSelect"
                                    value={selectedTool?.name || ""}
                                    onChange={
                                        (e) => {
                                            const tool = tools.find(t => t.name === e.target.value);
                                            if (tool) handleToolSelect(tool);
                                        }
                                    }
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Choose a tool...</option>
                                    {
                                        tools.map((tool) => (
                                            <option key={tool.name} value={tool.name}>
                                                {tool.name}
                                            </option>
                                        ))
                                    }
                                </select>
                            </div>
                            {
                                selectedTool && (
                                    <div className="space-y-4">
                                        <div className="bg-gray-50 p-4 rounded-md">
                                            <h4 className="font-semibold text-gray-900 mb-2">{selectedTool.name}</h4>
                                            {/* Tool Description */}
                                            {
                                                selectedTool.description && (
                                                    <div className="mb-4">
                                                        <p className="text-sm text-gray-700 leading-relaxed">
                                                            {selectedTool.description}
                                                        </p>
                                                    </div>
                                                )
                                            }
                                            {/* Formatted Arguments */}
                                            <div className="mb-4">
                                                {formatToolArguments(selectedTool.inputSchema)}
                                            </div>
                                            {/* Input Schema Dropdown */}
                                            {
                                                selectedTool.inputSchema && (
                                                    <details className="mt-3">
                                                        <summary className="text-sm font-medium text-blue-600 cursor-pointer hover:text-blue-800 flex items-center gap-1">
                                                            <svg className="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                            View Raw Input Schema
                                                        </summary>
                                                        <div className="mt-3 bg-gray-800 p-3 rounded-md overflow-x-auto">
                                                            <pre className="text-xs text-gray-200">
                                                                {JSON.stringify(selectedTool.inputSchema, null, 2)}
                                                            </pre>
                                                        </div>
                                                    </details>
                                                )
                                            }
                                        </div>
                                        <div>
                                            <label htmlFor="toolArgs" className="block text-sm font-medium text-gray-700 mb-1">
                                                Arguments (JSON)
                                            </label>
                                            <textarea
                                                id="toolArgs"
                                                value={toolArgs}
                                                onChange={(e) => setToolArgs(e.target.value)}
                                                placeholder='{"param1": "value1", "param2": "value2"}'
                                                rows={3}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                                            />
                                        </div>
                                        <button
                                            onClick={handleExecuteTool}
                                            disabled={isExecuting}
                                            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                        >
                                            {
                                                isExecuting
                                                    ? (
                                                        <>
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Executing...
                                                        </>
                                                    )
                                                    : (
                                                        "Execute Tool"
                                                    )
                                            }
                                        </button>
                                        {
                                            error && (
                                                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                                                    <p className="text-sm text-red-800">Error: {error}</p>
                                                </div>
                                            )
                                        }
                                        {
                                            toolResult && (
                                                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                                                    <h4 className="font-medium text-green-800 mb-2">Tool Result:</h4>
                                                    <pre className="text-sm bg-white p-2 rounded border overflow-x-auto">
                                                        {JSON.stringify(toolResult, null, 2)}
                                                    </pre>
                                                </div>
                                            )
                                        }
                                    </div>
                                )
                            }
                        </div>
                    )
            }
        </div>
    );
};